/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/invoice/invoice-list/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import {
  PiWarningOctagonDuotone as AlertOctagon,
  PiClockCounterClockwiseDuotone as AuditIcon,
  PiEyeDuotone as EyeIcon,
  PiFileTextDuotone as CreditNoteIcon,
  PiPrinterDuotone as PrintIcon,
  PiSealCheckDuotone as RegistrationIcon,
  PiXCircleDuotone as CancelIcon,
} from "react-icons/pi";
import {
  ComplianceStatusBadge,
  type ComplianceState,
  type ComplianceSubject,
  AvatarCard,
  getStatusBadge,
} from "@steelyard/theme";
import { Alert, Badge } from "rizzui";
import { formatDate } from "../../lib/format";
import { IRN_STATE, TALLY_STATE, isCancelled, money, statusWords, type InvoiceRow } from "../../lib/services/invoicing";
import { RowActions } from "../../components/table/row-actions";
import { invoicePath, orderPath } from "../../lib/paths";

/** Shared with Detail.tsx's own compliance badge, so the unknown-status fallback never diverges. */
function StatusCell({ subject, status, map }: { subject: ComplianceSubject; status: string; map: Readonly<Record<string, ComplianceState>> }) {
  const state = map[status];
  if (!state) {
    return (
      <Badge variant="outline" rounded="md">
        {statusWords(status)}
      </Badge>
    );
  }
  return <ComplianceStatusBadge subject={subject} state={state} />;
}

/**
 * Sizes are the template's own values: 300 for the name/customer identity cell, 200 for a date,
 * 150 for a short field, 140 for status. No selection column — no bulk action exists on this list
 * or anywhere in `apps/admin` today.
 */
export const invoiceColumns = (mayCancel: boolean, mayReadRegistration: boolean): ColumnDef<InvoiceRow>[] => [
  {
    id: "invoiceNumber",
    accessorKey: "customerName",
    header: "Customer",
    size: 300,
    cell: ({ row }) => (
      <Link to={invoicePath(row.original.invoiceNumber)} className="hover:underline">
        <AvatarCard src="" name={row.original.customerName} description={row.original.invoiceNumber} />
      </Link>
    ),
  },
  {
    id: "orderNumber",
    // An accessor, so the column sorts and the page search matches it. The link is the order the
    // invoice was raised from, by Billing's own orderId — never matched up by number.
    accessorKey: "orderNumber",
    header: "Order",
    size: 160,
    cell: ({ row }) =>
      row.original.orderId ? (
        <Link to={orderPath(row.original.orderNumber || row.original.orderId)} className="font-medium text-gray-900 hover:underline">
          {row.original.orderNumber ?? "View order"}
        </Link>
      ) : (
        <span className="text-gray-500">No order</span>
      ),
  },
  {
    id: "invoiceDate",
    accessorKey: "invoiceDate",
    header: "Date",
    size: 160,
    cell: ({ row }) => <span className="text-gray-600">{formatDate(row.original.invoiceDate)}</span>,
  },
  {
    id: "grandTotal",
    // `grandTotal` is already the parsed API figure, so the sort compares amounts. Nothing added up.
    accessorKey: "grandTotal",
    header: "Total",
    size: 150,
    meta: { align: "right" },
    cell: ({ row }) => <span className="font-semibold tabular-nums text-gray-900">{money(row.original.grandTotal)}</span>,
  },
  {
    id: "state",
    accessorKey: "state",
    header: "State",
    size: 140,
    cell: ({ row }) => {
      const cancelled = isCancelled(row.original.state);
      return (
        <span className="flex flex-col gap-1">
          <span className={cancelled ? "line-through" : undefined}>{getStatusBadge(row.original.state)}</span>
          {/* D-40: a draft waiting on a Super Admin — nothing has gone to IRN, Tally or the ledger yet. */}
          {row.original.reviewPending && !cancelled ? (
            <Badge variant="flat" color="warning" size="sm" rounded="pill">
              Draft · review
            </Badge>
          ) : null}
        </span>
      );
    },
  },
  {
    id: "irnStatus",
    header: "IRN",
    // Wide enough that "IRN not applicable" stays one line.
    size: 200,
    cell: ({ row }) => <StatusCell subject="irn" status={row.original.irnStatus} map={IRN_STATE} />,
  },
  {
    id: "ewayBillStatus",
    accessorKey: "ewayBillStatus",
    header: "E-way bill",
    size: 150,
    // Billing's own recorded status, worded as sent; none recorded means none was attempted.
    cell: ({ row }) =>
      row.original.ewayBillStatus ? (
        getStatusBadge(row.original.ewayBillStatus)
      ) : (
        <span className="text-gray-500">None</span>
      ),
  },
  {
    id: "tallySyncStatus",
    header: "Tally",
    size: 200,
    // Billing never sets a "failed" Tally status (a refused voucher is a failed QUEUE row, read on the
    // Tally sync queue and its unsynced tile), so there is no per-invoice failure link to draw here.
    cell: ({ row }) => <StatusCell subject="tally" status={row.original.tallySyncStatus} map={TALLY_STATE} />,
  },
  // No delete: invoices are retained eight years for compliance (see the Invoices list's own
  // banner) and are cancelled through a reasoned workflow, not removed from the list. Cancel… opens
  // the detail page's own `?cancel=1` ReasonDialog, under the same `billing.creditnote` condition its
  // header button uses — the window check and the reason both stay there. Print is the
  // chrome-free print route, in a new tab.
  {
    id: "actions",
    header: "",
    size: 110,
    cell: ({ row }) => (
      <RowActions
        name={row.original.invoiceNumber}
        primary={{ label: "View invoice", icon: EyeIcon, to: invoicePath(row.original.invoiceNumber) }}
        more={[
          { label: "Print invoice", icon: PrintIcon, to: `${invoicePath(row.original.invoiceNumber)}/print`, newTab: true },
          { label: "Audit trail", icon: AuditIcon, to: `${invoicePath(row.original.invoiceNumber)}/audit` },
          mayReadRegistration && { label: "E-invoice registration", icon: RegistrationIcon, to: `${invoicePath(row.original.invoiceNumber)}/registration` },
          mayCancel && !isCancelled(row.original.state) && {
            label: "Raise credit note",
            icon: CreditNoteIcon,
            to: `/invoicing/credit-notes?invoiceId=${row.original.id}`,
          },
          mayCancel && !isCancelled(row.original.state) && {
            label: "Cancel invoice…",
            icon: CancelIcon,
            to: `${invoicePath(row.original.invoiceNumber)}?cancel=1`,
            danger: true,
          },
        ]}
      />
    ),
  },
];

/** BR-07 defect banner, rendered as an extra full-width row via `PagedTable`'s `subRow`. */
export function invoiceDefectBanner(invoice: InvoiceRow) {
  return (
    <div role="alert">
      <Alert color="danger" variant="flat" size="sm" icon={<AlertOctagon aria-hidden className="size-4" />}>
        <span className="text-red-dark text-sm font-medium">
          <strong className="font-semibold">Defect — this invoice reached Tally without an IRN.</strong> BR-07 requires the IRN first, so the
          Tally voucher carries it. IRN status is <span className="font-mono">{statusWords(invoice.irnStatus)}</span> and Tally sync status is{" "}
          <span className="font-mono">{statusWords(invoice.tallySyncStatus)}</span>. This cannot happen if the services are behaving; report it
          rather than acting on the voucher.
        </span>
      </Alert>
    </div>
  );
}
